import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { ShippingLineService } from 'src/app/services/shipping-line/shipping-line.service';
import { ShippingLineModalService } from 'src/app/services/shipping-line-modal/shipping-line-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-shipping-line',
  templateUrl: './add-edit-shipping-line.component.html',
  styleUrls: ['./add-edit-shipping-line.component.scss']
})
export class AddEditShippingLineComponent extends DestructorComponent implements OnInit {

  @Output() shippingLineAdded = new EventEmitter<void>();
  @Output() shippingLineUpdated = new EventEmitter<void>();

  shippingLineForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedShippingLine: any = null;

  constructor(
    private fb: FormBuilder,
    private _shippingLineService: ShippingLineService,
    private _shippingLineModalService: ShippingLineModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.shippingLineForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      booking_agent: [''],
      type: ['roro', [Validators.required]]
    });
  }

  // Getter methods for form controls
  get name() { return this.shippingLineForm.get('name'); }
  get booking_agent() { return this.shippingLineForm.get('booking_agent'); }
  get type() { return this.shippingLineForm.get('type'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_shipping_line_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceShippingLine = this._shippingLineModalService.getSelectedShippingLine();
        if (serviceShippingLine) {
          this.isEditMode = true;
          this.selectedShippingLine = serviceShippingLine;
          this.populateForm(this.selectedShippingLine);
        } else {
          this.isEditMode = false;
          this.selectedShippingLine = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._shippingLineModalService.clearSelectedShippingLine();
        this.resetForm();
      });
    }
  }

  private populateForm(shippingLine: any): void {
    this.shippingLineForm.patchValue({
      name: shippingLine.name || '',
      booking_agent: shippingLine.booking_agent || '',
      type: shippingLine.type || 'roro'
    });
  }

  private resetForm(): void {
    this.shippingLineForm.reset();
    this.shippingLineForm.patchValue({
      type: 'roro' // Set default type
    });
    this.isLoading = false;
    this.markFormGroupUntouched(this.shippingLineForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_shipping_line_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.shippingLineForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.shippingLineForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        
          submitData[key] = formData[key];
        
      });

      const apiCall = this.isEditMode
        ? this._shippingLineService.UpdateData(this.selectedShippingLine._id, submitData)
        : this._shippingLineService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Shipping line operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Shipping line has been successfully updated!"
                : "Shipping line has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.shippingLineUpdated.emit();
                } else {
                  this.shippingLineAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Shipping line operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.shippingLineForm);
    }
  }
}
